interface Props {
  activeFilter?: string;
}

export default function EditorsPick({ activeFilter }: Props) {
  const isPetFilterActive = activeFilter === 'Pet Friendly';

  return (
    <section className="bg-[#F5F1EB] py-20 px-6 lg:px-14">
      <div className="max-w-7xl mx-auto">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-10">
          <span className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost">/ Top Recommendation</span>
          <div className="flex-1 h-px bg-[#C9A96E]/30" />
          {isPetFilterActive && (
            <span className="flex items-center gap-1.5 bg-[#2D4A3E] text-white text-xs tracking-[0.15em] uppercase font-jost px-4 py-1.5 rounded-full">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-paw-print-line text-xs" />
              </div>
              Pet Friendly Pick
            </span>
          )}
        </div>

        {/* Featured card */}
        <article className={`relative bg-white rounded-3xl overflow-hidden flex flex-col lg:flex-row transition-all duration-300 ${isPetFilterActive ? 'ring-2 ring-[#2D4A3E]' : ''}`}>
          {/* Pet filter banner */}
          {isPetFilterActive && (
            <div className="w-full bg-[#2D4A3E] text-white text-xs font-jost tracking-[0.15em] uppercase px-6 py-2.5 flex items-center gap-2 absolute top-0 left-0 right-0 z-30">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-paw-print-fill text-[#C9A96E] text-xs" />
              </div>
              Glen Falls Luxury Cabin is pet friendly — included in your filtered results below
            </div>
          )}

          {/* Badge */}
          <div className={`absolute ${isPetFilterActive ? 'top-14' : 'top-6'} left-6 z-20 flex items-center gap-2 bg-[#2D4A3E] text-white text-xs tracking-[0.2em] uppercase font-jost px-4 py-2 rounded-full transition-all duration-300`}>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-award-line text-[#C9A96E] text-xs" />
            </div>
            Top Recommendation
          </div>

          {/* Image */}
          <div className={`lg:w-[52%] h-80 lg:h-auto min-h-[420px] overflow-hidden shrink-0 relative ${isPetFilterActive ? 'mt-9 lg:mt-9' : ''}`}>
            <img
              src="https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/ba0d51886e955561a1eff3aa82f68703.jpeg"
              alt="Glen Falls Luxury Cabin exterior in fall with colorful autumn trees in Highlands NC"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10" />
          </div>

          {/* Content */}
          <div className={`flex-1 p-10 lg:p-14 flex flex-col justify-between ${isPetFilterActive ? 'lg:mt-9' : ''}`}>
            <div>
              <span className="text-[#6B8C7D] text-xs tracking-widest uppercase font-jost block mb-2">
                Luxury Cabin &nbsp;&bull;&nbsp; Highlands, NC — Near Glen Falls Waterfall
              </span>
              <h2 className="font-cormorant text-5xl text-[#1A1A18] font-medium leading-tight mb-3">
                Glen Falls Luxury Cabin<br />
                <span className="font-cormorant text-2xl text-[#6B8C7D] font-light not-italic">in Highlands, NC</span>
              </h2>

              {/* Rating row */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-[#C9A96E] text-sm" />
                    </div>
                  ))}
                </div>
                <span className="font-cormorant text-xl text-[#2D4A3E] font-medium">5.0</span>
                <span className="text-[#9C9890] text-xs font-jost bg-[#F5F1EB] px-3 py-1 rounded-full">
                  Top 5% Airbnb Guest Favorite
                </span>
              </div>

              <p className="text-[#5C5850] text-sm font-jost leading-relaxed mb-4">
                Glen Falls Luxury Cabin in Highlands, NC offers a unique blend of privacy, comfort, and location — just minutes from one of Highlands&apos; most beautiful waterfalls. Thoughtfully designed and surrounded by nature, it provides a peaceful retreat while still being close to town.
              </p>
              <p className="text-[#5C5850] text-sm font-jost leading-relaxed mb-8">
                With a consistent 5.0-star rating and recognition as an Airbnb Guest Favorite, Glen Falls Luxury Cabin in Highlands, NC stands among the top homes in the area for guests seeking a refined and relaxing mountain experience.
              </p>

              {/* Booking CTA */}
              <div className="mb-8">
                <p className="text-[#5C5850] text-xs font-jost tracking-wide uppercase mb-3">
                  Check availability and book your stay:
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://www.airbnb.com/rooms/900012473285450950"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap inline-flex items-center gap-2 bg-[#2D4A3E] text-white text-xs font-jost tracking-[0.12em] uppercase px-5 py-2.5 rounded-full cursor-pointer hover:bg-[#1e3329] transition-colors duration-200"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-external-link-line text-xs" />
                    </div>
                    View on Airbnb
                  </a>
                  <a
                    href="https://glenfallscabin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap inline-flex items-center gap-2 border border-[#C9A96E] text-[#5C5850] text-xs font-jost tracking-[0.12em] uppercase px-5 py-2.5 rounded-full cursor-pointer hover:bg-[#C9A96E]/10 transition-colors duration-200"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-home-3-line text-xs" />
                    </div>
                    Book Direct
                  </a>
                  <a
                    href="https://www.vrbo.com/3557955"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap inline-flex items-center gap-2 border border-[#C9A96E] text-[#5C5850] text-xs font-jost tracking-[0.12em] uppercase px-5 py-2.5 rounded-full cursor-pointer hover:bg-[#C9A96E]/10 transition-colors duration-200"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-external-link-line text-xs" />
                    </div>
                    Book on Vrbo
                  </a>
                </div>
                <p className="text-[#9C9890] text-xs font-jost mt-2.5">
                  Also available on Vrbo or book directly at glenfallscabin.com.
                </p>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-8">
                {['Pet Friendly', 'Near Glen Falls', 'Outdoor Wood-Burning Fireplace', 'Peaceful Setting', 'Close to Downtown Highlands'].map((feat) => (
                  <span
                    key={feat}
                    className={`text-xs font-jost px-3 py-1.5 rounded-full transition-all duration-300 ${
                      feat === 'Pet Friendly' && isPetFilterActive
                        ? 'bg-[#2D4A3E] text-white'
                        : 'bg-[#EDE8E0] text-[#5C5850]'
                    }`}
                  >
                    {feat === 'Pet Friendly' && isPetFilterActive && (
                      <i className="ri-paw-print-line mr-1 text-[#C9A96E]" />
                    )}
                    {feat}
                  </span>
                ))}
              </div>
            </div>

            {/* Footer row */}
            <div className="flex items-center justify-between pt-6 border-t border-[#F5F1EB]">
              <div className="flex items-center gap-3">
                <span className={`flex items-center gap-1.5 text-xs font-jost px-3 py-1.5 rounded-full transition-all duration-300 ${
                  isPetFilterActive
                    ? 'bg-[#2D4A3E] text-white'
                    : 'text-[#6B8C7D] bg-[#EEF5EE]'
                }`}>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-paw-print-line text-xs" />
                  </div>
                  Pet Friendly
                </span>
                <span className="flex items-center gap-1.5 text-[#3A6A8C] text-xs font-jost bg-[#EEF2F8] px-3 py-1.5 rounded-full">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-water-flash-line text-xs" />
                  </div>
                  Near Glen Falls
                </span>
              </div>
              <div className="text-right">
                <p className="text-[#9C9890] text-xs font-jost mb-0.5">Editor&apos;s top pick for</p>
                <p className="font-cormorant text-lg text-[#2D4A3E] font-medium italic">Authentic Highlands Experience</p>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}